import React, { useState } from "react";
import { ModalFooter, ModalFooterActions } from "@twilio-paste/modal";
import { ModalBody, Box } from "@twilio-paste/core";
import { Button } from "@twilio-paste/button";

import ModalInputField from "./ModalInputField";
import ConvoModal from "./ConvoModal";

interface NewMessageModalProps {
  isModalOpen: boolean;
  onCancel: () => void;
  // Gets the number as +1XXXXXXXXXX
  onSave: (recipient: string) => Promise<void>;
}

// "604-767-6678", "(604) 767 6678" or "+1 604 767 6678" -> "+16047676678"
function toE164(input: string): string | null {
  let digits = input.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? `+1${digits}` : null;
}

const NewMessageModal: React.FC<NewMessageModalProps> = (
  props: NewMessageModalProps
) => {
  const [number, setNumber] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setLoading] = useState(false);
  const recipient = toE164(number);

  const onCancel = () => {
    setNumber("");
    setError("");
    props.onCancel();
  };

  const onSave = async () => {
    if (!recipient || isLoading) {
      return;
    }
    setError("");
    setLoading(true);
    try {
      await props.onSave(recipient);
      setNumber("");
    } catch (e) {
      setError((e as Error).message ?? "");
    }
    setLoading(false);
  };

  return (
    <ConvoModal
      title="New Message"
      isModalOpen={props.isModalOpen}
      handleClose={onCancel}
      modalBody={
        <ModalBody>
          <Box
            as="form"
            onSubmit={(e: React.FormEvent<HTMLElement>) => {
              e.preventDefault();
              onSave();
            }}
          >
            <ModalInputField
              isFocused={true}
              label="To"
              input={number}
              placeholder="Phone number, e.g. 604-555-1234"
              onChange={(s) => {
                setNumber(s);
                setError("");
              }}
              error={
                error ||
                (number.replace(/\D/g, "").length >= 10 && !recipient
                  ? "Enter a 10-digit US or Canadian number."
                  : "")
              }
            />
          </Box>
        </ModalBody>
      }
      modalFooter={
        <ModalFooter>
          <ModalFooterActions>
            <Button disabled={isLoading} variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              disabled={!recipient || isLoading}
              variant="primary"
              onClick={onSave}
            >
              Start
            </Button>
          </ModalFooterActions>
        </ModalFooter>
      }
    />
  );
};

export default NewMessageModal;
